import { repositories } from '../../api/Repositories';
import {ActionTree} from "vuex";
import {RootState} from "../types/RootState";
import {ExpertRatingsState} from "../types/ExpertRatingsState";
import {RepositoryFabric} from '@api/repositories/RepositoryFabric'
import {ObjectRepository} from '@api/repositories/ObjectRepository';
import {QueryParametersInterface} from "@api/repositories/interfaces/QueryParametersInterface";
import { UserRepository } from '@api/repositories/UserRepository';
import {PROFILE_FIELDS_BASIC, UserProfile} from '@api/models/UserProfile';
import Vue from 'vue';
import { Project } from '@share/expert-ratings/types/Project';
import { Element } from '@api/models/Element';
import { Criteria } from '@share/expert-ratings/types/Criteria';
import { groupFilter } from "@api/helpers/groupFilter";
import { ExpertRatingsDataHelper as DataHelper } from '@mixins/ExpertRatingsDataHelper'
import { AxiosError } from "axios";
import { getUnknownProfileErrorMessage, showDataErrorModal } from '@share/expert-ratings/helpers/wrongDataHandler';

export const actions: ActionTree<ExpertRatingsState, RootState> = {
    async loadSections({ commit, rootGetters, dispatch }) {
        const expertRatingsParams = rootGetters['events/selectedEventViewParam']('expertRatings', null);

        try {
            const query: QueryParametersInterface = {
                take: -1,
                include: ['id', 'updatedAt', 'title', 'userProfileIds', 'chairmanProfileId', 'additionalRatingCriterias', 'ratingsResetEnabled'],
                where: {
                    parentId: {
                        $in: expertRatingsParams?.parentIds
                    }
                },
                order: {
                    title: 'asc'
                }
            };

            if (!rootGetters['isAdmin']) {
                query.where.userProfileIds = {
                    $containsAny: [rootGetters['userProfileId']]
                }
            } else {
                // У организаторов сейчас есть общая группа "Горизонты 2020", которая не позволит фильтровать контент привычным образом
                const ignoreGroupIds: string[] = expertRatingsParams && expertRatingsParams?.ignoreCommonGroups
                    ? expertRatingsParams.ignoreCommonGroups
                    : [];

                const userGroupIds: string[] = rootGetters['groupIds'].filter((item: string) => !ignoreGroupIds.includes(item));
                query.where.$and = groupFilter(userGroupIds);
            }

            let list = await (repositories.get(RepositoryFabric.OBJECT) as ObjectRepository).query(process.env.VUE_APP_SECTIONS_AND_REPORT_SCHEMA!, query);

            const profileIds = [...new Set(list.map(item => item.userProfileIds).flat())].filter(item => item);
            await dispatch('loadProfiles', profileIds);

            commit('setSections', list);
        }
        catch (e) {
            dispatch('reportException', e, {root: true});
        }
    },
    setSelected({commit}, value: string) {
        commit('setSelected', value);
    },
    async updateCurrentSection({commit, getters, rootGetters}) {
        if (getters['selectedSectionId'] === null) {
            return;
        }

        const section = (await (repositories.get(RepositoryFabric.OBJECT) as ObjectRepository).query(process.env.VUE_APP_SECTIONS_AND_REPORT_SCHEMA!, {
            where: {
                id: getters['selectedSectionId']
            },
            include: ['id', 'updatedAt', 'title', 'userProfileIds', 'chairmanProfileId', 'additionalRatingCriterias', 'ratingsResetEnabled']
        })).pop();

        const selectedSection = getters['selectedSection'];

        if (section && section.updatedAt && !section.updatedAt.equals(selectedSection?.updatedAt)) {
            commit('updateSection', {
                section: section,
                isChairman: section?.chairmanProfileId === rootGetters['userProfileId']
            });
        }
    },
    async loadCriterias({ commit, getters, dispatch }, force = false) {
        try {
            if (getters['criterias'].length == 0 || force) {
                const list = await (repositories.get(RepositoryFabric.OBJECT) as ObjectRepository).query(process.env.VUE_APP_EXPERT_RATING_CRITERIAS_SCHEMA!, {
                    order: {
                        orderIndex: 'asc'
                    },
                    where: {
                        $or: [
                            {
                                sectionId: {
                                    $exists: false
                                }
                            },
                            {
                                sectionId: getters['selectedSectionId']
                            }
                        ]
                    },
                    take: -1
                });

                const resultList = DataHelper.prepareCommonCriteria(list);

                commit('setCriterias', resultList);
            }

            const section = getters['selectedSection'];
            const additionalCriterias: (string | null)[] = [];
            [0,1,2].forEach(index => {
                let additionalRatingCriteria = section && section?.additionalRatingCriterias && section.additionalRatingCriterias[index];

                additionalRatingCriteria = additionalRatingCriteria === "null" ? null : additionalRatingCriteria;

                additionalCriterias[index] = additionalRatingCriteria
            });

            commit('setAdditionalCriterias', additionalCriterias);
        }
        catch (e) {
            dispatch('reportException', e, { root: true });
        }
    },
    setAdditionalCriteria({commit, getters }, data) {

        if (data.value) {
            commit('setAdditionalCriteria', data);
        }
        else {
            const criterias = getters.additionalCriterias;
            const hasValueAfter = criterias.some((item: { title: string }, index: number) => index > data.index && item && item.title);

            if (data.index == criterias.length - 1 || !hasValueAfter) {
                commit('setAdditionalCriteria', data);
            }
            else //если значение не последнее или последующие ячейки имеют значение
            {
                const array = criterias.reduce((acc: string[], next: Criteria, index: number) => {

                    let current = next.title;

                    if (index == 1 && data.index == 0) {
                        acc[index - 1] = current;
                    }
                    else if (index > data.index) {
                        acc[index - 1] = current;
                        current = data.value
                    }

                    acc.push(current);

                    return acc;
                }, [])

                commit('setAdditionalCriterias', array);
            }
        }

    },
    async saveAdditionalCriteria({getters}) {
        const section = getters['selectedSection'];

        if (!section) {
            return;
        }

        await (repositories.get(RepositoryFabric.OBJECT) as ObjectRepository).update(process.env.VUE_APP_SECTIONS_AND_REPORT_SCHEMA!, section.id, {
            additionalRatingCriterias: getters['additionalCriterias'].map((item: Criteria | null) => {
                return item ? item.title : null;
            })
        });
    },
    async saveRatingsResetEnabled({ getters }, value: boolean) {
        const section = getters['selectedSection'];

        if (!section) {
            return;
        }

        Vue.set(section, 'ratingsResetEnabled', value);

        await (repositories.get(RepositoryFabric.OBJECT) as ObjectRepository).update(process.env.VUE_APP_SECTIONS_AND_REPORT_SCHEMA!, section.id, {
            ratingsResetEnabled: value
        });
    },
    async loadProjects({ commit, dispatch }, section: string) {
        try {
            const list = await (repositories.get(RepositoryFabric.OBJECT) as ObjectRepository).query(process.env.VUE_APP_SECTIONS_AND_REPORT_SCHEMA!, {
                where: {
                    parentId: section
                },
                include: [
                    'id', 'title', 'userProfileIds', 'speakingOrder', 'updatedAt'
                ],
                take: -1
            })

            const profileIds = [...new Set(list.map(item => item.userProfileIds).flat())].filter(item => item);
            await dispatch('loadProfiles', profileIds);

            commit('setSectionProjects', list.map((project) => {
                    project.totalRating = null;
                    return Vue.observable(project);
                })
            );
        }
        catch (e) {
            dispatch('reportException', e, { root: true });
        }
    },
    async loadProfiles({ commit, rootGetters }, profileIds: string[]) {
        let profiles = await (repositories.get(RepositoryFabric.USER) as UserRepository).profiles({
            take: -1,
            skip: 0,
            where: {
              id: { $in: profileIds }
            },
            include: [...PROFILE_FIELDS_BASIC, 'middleName']
        });

        profiles.forEach((profile: UserProfile) => {
            if (!rootGetters['expertRatings/profile'](profile.id)) {
                commit('addProfile', profile);
            }
        })
    },
    initSectionRatings({commit, getters, dispatch}, sectionId: string) {
        dispatch('loadFromStorage', sectionId);

        const sectionRatings = getters['sectionRatings'](sectionId);
        const projects = getters['sectionProjects'];
        const additionalCriterias = getters['additionalCriterias'];
        const commonCriterias = getters['criterias'];
        const ADDITIONAL_CRITERIA_AMOUNT = 3;
        const activeAdditionalCriteriasCount = additionalCriterias.filter((item: Criteria) => item && item.title).length;
        const inactiveAdditionalCriteriasCount = ADDITIONAL_CRITERIA_AMOUNT - activeAdditionalCriteriasCount;

        //удаление рейтинга проекта которого нет в списке пришедших проектов и сборс соответствующих оценок если доп критерий не активный
        for (const sectionProjectId in sectionRatings) {
            if (Object.prototype.hasOwnProperty.call(sectionRatings, sectionProjectId)) {

                let projectRatings = sectionRatings[sectionProjectId];

                //дополнение списка с оценками для избежание стирания
                if (commonCriterias.length + additionalCriterias.length > projectRatings.length) {
                    const emptyRatings = Array(commonCriterias.length + additionalCriterias.length - projectRatings.length).fill(null);
                    sectionRatings[sectionProjectId] = [...sectionRatings[sectionProjectId], ...emptyRatings];

                    projectRatings = sectionRatings[sectionProjectId];
                }

                const requiredRatesCount = projectRatings.length - inactiveAdditionalCriteriasCount;

                sectionRatings[sectionProjectId] = projectRatings.map((item: number | null, index: number) => {
                    return index < requiredRatesCount ? item : null;
                });

                const ratingNotExistInProject = projects.every((project: Project) => project.id !== sectionProjectId);

                if (ratingNotExistInProject) {
                    delete sectionRatings[sectionProjectId];
                }
            }
        }

        projects.forEach((project: Project) => {
            if (!Object.prototype.hasOwnProperty.call(sectionRatings, project.id)) {
                sectionRatings[project.id] = [...commonCriterias, ...additionalCriterias].map((item: Element[]) => null);
            } else {
                dispatch('updateProjectTotalRate', {
                    sectionId: sectionId,
                    project
                })
            }
        });

        commit('setSectionRatings', {
            sectionId: sectionId,
            ratings: sectionRatings
        });

        dispatch('setCanRateCurrentSection', sectionId);
        dispatch('checkExpertRatingsSaved');
    },
    updateProjectTotalRate({getters, commit, state}, data) {
        const sectionRatings = getters['sectionRatings'](data.sectionId);

        if (sectionRatings && Object.prototype.hasOwnProperty.call(sectionRatings, data.project.id)) {
            const rates = sectionRatings[data.project.id].filter((rate: number|null) => rate);
            if (rates.length) {
                Vue.set(data.project, 'totalRate', Math.round(rates.reduce((sum: number, value: number) => sum + value) / rates.length * 100) / 100);
            } else {
                Vue.set(data.project, 'totalRate', null);
            }
        }
    },
    setCanRateCurrentSection({ state, getters, commit }, sectionId: string) {
        const sectionRatings = getters['sectionRatings'](sectionId);
        const criteriasCount = state.criterias.length + state.additionalCriterias.filter(item => item && item.title).length;
        let canRateSection = true;

        if (sectionRatings) {
            for (const index in sectionRatings) {
                if (Object.prototype.hasOwnProperty.call(sectionRatings, index)) {

                    const projectRatings = sectionRatings[index]?.filter((rate: number | null) => rate);

                    if (projectRatings.length < criteriasCount) {
                        canRateSection = false;
                        break;
                    }
                }
            }
        }

        commit('setCanRateCurrentSection', canRateSection);
    },
    setProjectRating({commit, getters, dispatch}, data) {
        commit('setProjectRating', data);
        dispatch('saveToStorage', data.sectionId);
    },
    saveToStorage({getters}, sectionId: string) {
        const ratings = getters['sectionRatings'](sectionId);
        localStorage.setItem('ratings-' + sectionId, JSON.stringify(ratings));
    },
    loadFromStorage({commit}, sectionId: string) {
        const stored = localStorage.getItem('ratings-' + sectionId);
        if (stored) {
            commit('setSectionRatings', {
                sectionId: sectionId,
                ratings: JSON.parse(stored)
            })
        }
    },
    removeLocalStorageItems({ state, rootGetters }) {
        //rates
        state.sections.forEach((item) => {
            localStorage.removeItem('ratings-' + item.id);
        });

        //comments
        localStorage.removeItem("ratings-comments-" + rootGetters['userProfileId']);
        localStorage.removeItem(`ratings-summary-comments-${rootGetters['userProfileId']}`);
    },
    async setPosition({ commit, dispatch }, { id, position }) {
        try {
            await (repositories.get(RepositoryFabric.OBJECT) as ObjectRepository).update(process.env.VUE_APP_SECTIONS_AND_REPORT_SCHEMA!, id, {
                speakingOrder: position
            });
            commit('setPosition', { id, position });
        } catch (e) {
            dispatch('reportException', e, { root: true });
        }
    },
    async saveExpertRates({getters, rootGetters, commit, dispatch}) {
        if (!getters['selectedSection']) {
            return false;
        }

        const resubmitEnabled: boolean = getters['selectedSection']?.ratingsResetEnabled === true;
        const sectionRatings = getters['sectionRatings'](getters['selectedSection'].id);
        const comments = getters['expertComments'](rootGetters['userProfileId']);
        const projectIds = Object.keys(sectionRatings);

        try {
            const saved: Record<string, string> = {};

            (await (repositories.get(RepositoryFabric.OBJECT) as ObjectRepository).query(process.env.VUE_APP_EXPERT_RATING_SCHEMA!, {
                where: {
                    projectId: {
                        $in: projectIds
                    },
                    expertId: rootGetters['userProfileId']
                },
                take: -1
            })).map((rate: Element) => {
                saved[rate.projectId] = rate.id;
            });

            for (let projectId in sectionRatings) {
                if (saved[projectId] && !resubmitEnabled) {
                    continue;
                }

                const projectRatings = sectionRatings[projectId];

                if (saved[projectId]) {
                    (repositories.get(RepositoryFabric.OBJECT) as ObjectRepository).update(process.env.VUE_APP_EXPERT_RATING_SCHEMA!, saved[projectId], {
                        ratings: projectRatings,
                        comment: comments[projectId] ?? null
                    })
                } else {
                    (repositories.get(RepositoryFabric.OBJECT) as ObjectRepository).create(process.env.VUE_APP_EXPERT_RATING_SCHEMA!, {
                        projectId: projectId,
                        expertId: rootGetters['userProfileId'],
                        ratings: projectRatings,
                        comment: comments[projectId] ?? null
                    })
                }
            }

            commit('setCurrentSectionRatesSaved', true);
        }
        catch(e) {

            console.dir(e)

            if (
                (e instanceof Error && e.message === "Network Error") ||
                ((e as AxiosError).isAxiosError && (e as AxiosError).code === "ECONNABORTED")
            ) {
                throw e;
            }
        }
    },
    async checkExpertRatingsSaved({ getters, rootGetters, commit}) {
        if (!getters['selectedSection']) {
            return false;
        }

        const sectionRatings = getters['sectionRatings'](getters['selectedSection'].id);
        const projectIds = Object.keys(sectionRatings);

        if (projectIds.length) {
            const saved = await (repositories.get(RepositoryFabric.OBJECT) as ObjectRepository).query(process.env.VUE_APP_EXPERT_RATING_SCHEMA!, {
                where: {
                    projectId: {
                        $in: projectIds
                    },
                    expertId: rootGetters['userProfileId']
                },
                take: -1
            });

            if (saved.length == projectIds.length) {
                commit('setCurrentSectionRatesSaved', true);
            }
            else {
                commit('setCurrentSectionRatesSaved', false);
            }
        }
    },
    async loadProjectsRatings({getters, commit}, filter) {
        if (!filter.projectIds.length) {
            return;
        }

        let query: QueryParametersInterface = {
            where: {
                projectId: {
                    $in: filter.projectIds
                }
            },
            take: -1
        };

        if (Object.prototype.hasOwnProperty.call(filter, 'expertId')) {
            query.where.expertId = filter.expertId;
        }

        const ratings = await (repositories.get(RepositoryFabric.OBJECT) as ObjectRepository).query(process.env.VUE_APP_EXPERT_RATING_SCHEMA!, query);

        const mapRatings: Record<string, Record<string, number>> = {};
        const mapExpertComments: Record<string, Record<string, string>> = {};
        ratings.forEach(ratingElement => {
            if (ratingElement.ratings.length) {
                if (!Object.prototype.hasOwnProperty.call(mapRatings, ratingElement.projectId)) {
                    mapRatings[ratingElement.projectId] = {};
                }
                if (!Object.prototype.hasOwnProperty.call(mapRatings[ratingElement.projectId], ratingElement.expertId)) {
                    const notNullRatings = ratingElement.ratings.filter((rate: number) => rate !== null);
                    const rate = Math.round(notNullRatings.reduce((sum: number, value: number) => sum + value) / notNullRatings.length * 100) / 100;
                    mapRatings[ratingElement.projectId][ratingElement.expertId] = rate;
                }

                //заполнение комментариев
                if (!Object.prototype.hasOwnProperty.call(mapExpertComments, ratingElement.projectId)) {
                    mapExpertComments[ratingElement.projectId] = {};
                }
                if (!Object.prototype.hasOwnProperty.call(mapExpertComments[ratingElement.projectId], ratingElement.expertId)) {
                    const comment = ratingElement.comment;
                    mapExpertComments[ratingElement.projectId][ratingElement.expertId] = comment;
                }
            }
        })

        commit('setExpertSummaryRatings', mapRatings);
        commit('setExpertSummaryComments', mapExpertComments);
    },
    setExpertComment({getters, commit}, data) {
        commit('setExpertComment', data);
        localStorage.setItem("ratings-comments-" + data.expertId, JSON.stringify(getters['expertComments'](data.expertId)));
    },
    setSummaryComment({getters, rootGetters, commit}, { projectId, value }) {
        commit('setSummaryComment', { projectId, value });
        localStorage.setItem(`ratings-summary-comments-${rootGetters['userProfileId']}`, JSON.stringify(getters['summaryComments']));
    },
    loadSummaryComments({ rootGetters, commit }) {
        const stored = localStorage.getItem(`ratings-summary-comments-${rootGetters['userProfileId']}`);
        if (stored) {
            const parsed = JSON.parse(stored);
            if (typeof parsed === 'object') {
                for (let projectId in parsed) {
                    commit('setSummaryComment', { projectId, value: parsed[projectId] });
                }
            }
        }
    },
    loadSavedComments({getters, rootGetters, commit}) {
        const profileId = rootGetters['userProfileId'];
        const stored = localStorage.getItem("ratings-comments-" + profileId);

        if (stored) {
            const parsed = JSON.parse(stored);
            if (typeof parsed === 'object') {
                for (let projectId in parsed) {
                    commit('setExpertComment', {
                        projectId: projectId,
                        expertId: profileId,
                        value: parsed[projectId]
                    });
                }
            }
        }
    },
    async saveProjectRates({getters, dispatch }, { data, dontInclude }) {
        if (!getters['selectedSection']) {
            return false;
        }

        try {
            for (let project of getters['sectionProjects']) {
                if (!dontInclude.includes(project.id)) {
                    await (repositories.get(RepositoryFabric.OBJECT) as ObjectRepository).create(process.env.VUE_APP_PROJECT_RATING_SCHEMA!, {
                        projectId: project.id,
                        rating: getters['projectRate'](project.id),
                        place: data[project.id].place || -1,
                        description: data[project.id].comment || '',
                        sectionId: getters['selectedSection'].id
                    })
                }
            }

            dispatch('saveRatingsResetEnabled', false);
        }
        catch(e) {
            console.dir(e)
            if (
                (e instanceof Error && e.message === "Network Error") ||
                ((e as AxiosError).isAxiosError && (e as AxiosError).code === "ECONNABORTED")
            ) {
                throw e;
            }
        }
    },
    showProfileError({ state }, unknownProfilesIds: string[]) {
        showDataErrorModal(getUnknownProfileErrorMessage(unknownProfilesIds.join(", ")));
        state.profileErrorShown = true;
    }
};

